/**
 * Authentication Incentive Modal — paywalls du funnel
 *
 * Doctrine pricing 09/09/2026 :
 * - Anonymous (limit=3) : a consommé ses 3 IA gratuites → CTA "Créer un compte
 *   gratuit" (garder son sondage, pas plus d'IA), rappel que le manuel reste
 *   illimité sans compte.
 * - Free (limit=3)      : a consommé ses 3 IA Free → CTA "Passer Solo" (30€/an
 *   pour IA illimitée + features).
 *
 * Anonyme et Free ont désormais la même limite (3) : le composant ne peut
 * plus les distinguer via `maxConversations`, il utilise `isAuthenticated`.
 */

import React from "react";
import {
  X,
  Crown,
  MessageCircle,
  Calendar,
  Sparkles,
  ExternalLink,
  PencilLine,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { type AuthIncentiveType } from "../../services/QuotaService";
import { Link } from "react-router-dom";
import { TRIAL_AI_CONVERSATIONS } from "../../constants/quotas";

type TFunc = (key: string, options?: Record<string, unknown>) => string;

interface AuthIncentiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignUp: () => void;
  onSignIn: () => void;
  trigger: AuthIncentiveType;
  /** Utilisateur déjà connecté (Free) vs anonyme — distingue Moment 1 / Moment 2. */
  isAuthenticated?: boolean;
  /**
   * Essai Solo en cours. Un essayeur qui heurte son plafond IA n'est NI un
   * anonyme NI un Gratuit : il est déjà `plan_type = 'solo'`, lui proposer
   * « Passez à Solo » n'aurait aucun sens, et son plafond n'est pas 3 mais
   * TRIAL_AI_CONVERSATIONS. Passé en prop plutôt que lu ici : ce composant est
   * purement présentationnel, comme le montre `isAuthenticated`.
   */
  isTrialActive?: boolean;
  /** Jours restants, pour dire au bon moment ce qui va s'arrêter. */
  trialDaysLeft?: number | null;
  currentUsage?: {
    conversations: number;
    maxConversations: number;
    polls?: number;
    maxPolls?: number;
  };
}

interface ModalContent {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  description: string;
  /** CTA principal (action positive forte). */
  primaryCta: {
    label: string;
    onClick: (handlers: { onSignUp: () => void }) => void;
    icon: React.ReactNode;
  };
  /** CTA secondaire (action moindre, garde le user dans le produit). */
  secondaryCta?: {
    label: string;
    onClick: (handlers: { onClose: () => void }) => void;
  };
  /** Lien tertiaire (sign-in pour les comptes existants). */
  signInLabel?: string;
}

function getModalContent(
  t: TFunc,
  trigger: AuthIncentiveType,
  isAuthenticated: boolean,
  isTrialActive: boolean,
  trialDaysLeft: number | null,
  trialAiLimit: number,
): ModalContent {
  // === Essai en cours : plafond IA atteint ===
  // Placé AVANT le Moment 2, qui se déclenche sur la même condition. L'ordre
  // est la garde : un essayeur passerait sinon par la branche Gratuit et
  // lirait « vos 3 conversations IA à vie », faux sur les deux points.
  if (
    (trigger === "conversation_limit" || trigger === "feature_unlock") &&
    isAuthenticated &&
    isTrialActive
  ) {
    return {
      title: t("auth_incentive.trial_title", { limit: trialAiLimit }),
      subtitle:
        trialDaysLeft !== null && trialDaysLeft > 0
          ? t("auth_incentive.trial_subtitle", { days: trialDaysLeft })
          : t("auth_incentive.trial_subtitle_last_day"),
      icon: <Crown className="w-12 h-12 text-primary" />,
      description: t("auth_incentive.trial_description"),
      primaryCta: {
        label: t("auth_incentive.trial_cta"),
        onClick: ({ onSignUp: _ }) => {
          window.location.href = "/pricing";
        },
        icon: <Crown className="w-4 h-4" />,
      },
      secondaryCta: {
        label: t("auth_incentive.trial_secondary_cta"),
        onClick: ({ onClose }) => onClose(),
      },
    };
  }

  // === Moment 1 : anonyme a consommé ses 3 IA ===
  if (
    (trigger === "conversation_limit" || trigger === "feature_unlock") &&
    !isAuthenticated
  ) {
    return {
      title: t("auth_incentive.anon_title"),
      subtitle: t("auth_incentive.anon_subtitle"),
      icon: <Sparkles className="w-12 h-12 text-primary" />,
      description: t("auth_incentive.anon_description"),
      primaryCta: {
        label: t("auth_incentive.anon_primary_cta"),
        onClick: ({ onSignUp }) => onSignUp(),
        icon: <Crown className="w-4 h-4" />,
      },
      secondaryCta: {
        label: t("auth_incentive.anon_secondary_cta"),
        onClick: ({ onClose }) => onClose(),
      },
      signInLabel: t("auth_incentive.sign_in_label"),
    };
  }

  // === Moment 2 : Free a consommé ses 3 IA ===
  if (
    (trigger === "conversation_limit" || trigger === "feature_unlock") &&
    isAuthenticated
  ) {
    return {
      title: t("auth_incentive.free_title"),
      subtitle: t("auth_incentive.free_subtitle"),
      icon: <Crown className="w-12 h-12 text-primary" />,
      description: t("auth_incentive.free_description"),
      primaryCta: {
        label: t("auth_incentive.solo_cta"),
        onClick: ({ onSignUp: _ }) => {
          window.location.href = "/pricing";
        },
        icon: <Crown className="w-4 h-4" />,
      },
      secondaryCta: {
        label: t("auth_incentive.free_secondary_cta"),
        onClick: ({ onClose }) => {
          onClose();
          window.location.href = "/pricing";
        },
      },
    };
  }

  // === Moment 3 : limite polls actifs (10 pour Free) ===
  if (trigger === "poll_limit") {
    return {
      title: t("auth_incentive.poll_limit_title"),
      subtitle: t("auth_incentive.poll_limit_subtitle"),
      icon: <Calendar className="w-12 h-12 text-warning" />,
      description: t("auth_incentive.poll_limit_description"),
      primaryCta: {
        label: t("auth_incentive.solo_cta"),
        onClick: ({ onSignUp: _ }) => {
          window.location.href = "/pricing";
        },
        icon: <Crown className="w-4 h-4" />,
      },
      secondaryCta: {
        label: t("auth_incentive.poll_limit_secondary_cta"),
        onClick: ({ onClose }) => {
          onClose();
          window.location.href = "/dashboard";
        },
      },
    };
  }

  // === Fallback générique ===
  return {
    title: t("auth_incentive.generic_title"),
    subtitle: t("auth_incentive.generic_subtitle"),
    icon: <MessageCircle className="w-12 h-12 text-primary" />,
    description: t("auth_incentive.generic_description"),
    primaryCta: {
      label: t("auth_incentive.generic_primary_cta"),
      onClick: ({ onSignUp }) => onSignUp(),
      icon: <Crown className="w-4 h-4" />,
    },
    signInLabel: t("auth_incentive.sign_in_label"),
  };
}

const AuthIncentiveModal: React.FC<AuthIncentiveModalProps> = ({
  isOpen,
  onClose,
  onSignUp,
  onSignIn,
  trigger,
  isAuthenticated = false,
  isTrialActive = false,
  trialDaysLeft = null,
  currentUsage,
}) => {
  const { t } = useTranslation("polls");

  if (!isOpen) return null;

  const content = getModalContent(
    t,
    trigger,
    isAuthenticated,
    isTrialActive,
    trialDaysLeft,
    TRIAL_AI_CONVERSATIONS,
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="relative p-6 text-center border-b border-border">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-accent rounded-full transition-colors"
            data-testid="authincentivemodal-button"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>

          <div className="flex justify-center mb-4">{content.icon}</div>

          <h2 className="text-xl font-bold text-foreground mb-2">
            {content.title}
          </h2>

          <p className="text-muted-foreground text-sm">{content.subtitle}</p>
        </div>

        <div className="p-6">
          <p className="text-foreground mb-6 text-center">
            {content.description}
          </p>

          {/* Lien d'info pricing/quotas */}
          <div className="mb-6">
            <div className="p-3 bg-background rounded-lg border border-border">
              <Link
                to="/pricing"
                className="flex items-center justify-center gap-2 text-primary hover:text-primary/80 transition-colors"
                onClick={onClose}
              >
                <ExternalLink className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {t("auth_incentive.view_plans_link")}
                </span>
              </Link>
            </div>
          </div>

          <div className="space-y-3" data-testid="auth-required">
            <button
              onClick={() => content.primaryCta.onClick({ onSignUp })}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="authincentivemodal-primary-cta"
            >
              {content.primaryCta.icon}
              {content.primaryCta.label}
            </button>

            {content.secondaryCta && (
              <button
                onClick={() => content.secondaryCta!.onClick({ onClose })}
                className="w-full bg-background hover:bg-accent text-foreground font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                data-testid="authincentivemodal-secondary-cta"
              >
                <PencilLine className="w-4 h-4" />
                {content.secondaryCta.label}
              </button>
            )}

            {content.signInLabel && (
              <button
                onClick={onSignIn}
                className="w-full text-muted-foreground hover:text-foreground font-medium py-2 px-4 transition-colors text-sm"
                data-testid="sign-in"
              >
                {content.signInLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthIncentiveModal;
